import { Link } from 'react-router'
import { Button, Card, Icon, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { CurrencyBadge } from '@/components/CurrencyBadge'
import { TransactionRow } from '@/components/TransactionRow'
import { Skeleton } from '@/components/States'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { BRAND } from '@/brand/brand'

const FEES = [
  { label: 'Between your own accounts', value: 'No fee' },
  { label: `To another ${BRAND.name} user`, value: 'No fee' },
  { label: 'Sending abroad', value: '1.0%' },
  { label: 'FX spread when converting', value: 'from 0.6%' },
  { label: 'Top up from a debit card', value: '0.5%' },
  { label: 'Top up from a credit card', value: '2.5%' },
]

export function TransfersScreen() {
  const repository = useRepository()
  const recipients = useQuery(() => repository.listRecipients(), [repository])
  const corridors = useQuery(() => repository.listCorridors(), [repository])
  const transactions = useQuery(() => repository.listTransactions({ limit: 5 }), [repository])

  const corridorOf = (id: string) => corridors.data?.find((corridor) => corridor.id === id)

  return (
    <>
      <PageHeader
        title="Transfers"
        subtitle={`Move money between your accounts, to another ${BRAND.name} user, or home.`}
        actions={
          <Button variant="primary" size="md" iconStart="north_east" to="/transfers/send">
            Send abroad
          </Button>
        }
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Send again
            </Text>
            <Stack gap={3} className="pb-mt-4">
              {recipients.status === 'loading' && <Skeleton height={160} />}
              {recipients.data?.map((recipient) => {
                const corridor = corridorOf(recipient.corridorId)
                return (
                  <Card key={recipient.id} tone="plain" interactive padded={false}>
                    <Stack direction="row" align="center" gap={3} style={{ padding: 'var(--pb-space-3)' }}>
                      {corridor && <CurrencyBadge currency={corridor.currency} size={36} />}
                      <Stack grow gap={0}>
                        <Text variant="caption" weight="semibold" truncate>
                          {recipient.name}
                        </Text>
                        <Text variant="eyebrow" tone="secondary" truncate>
                          {corridor?.country} · {recipient.maskedAccount}
                        </Text>
                      </Stack>
                      <Icon name="arrow_forward" size={16} />
                    </Stack>
                  </Card>
                )
              })}
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Stack gap={3}>
            {[
              { icon: 'swap_horiz', title: 'Between my accounts', body: 'Instant, and never a fee', to: '/convert' },
              { icon: 'bolt', title: `To another ${BRAND.name} user`, body: 'Seconds, no fee — an FX spread applies', to: '/transfers' },
              { icon: 'public', title: 'Send abroad', body: '1.0% plus the corridor spread', to: '/transfers/send' },
            ].map((action) => (
              <Card key={action.title} tone="light" interactive as={Link} to={action.to}>
                <Stack direction="row" align="center" gap={3}>
                  <Icon name={action.icon} size={20} />
                  <Stack grow gap={0}>
                    <Text variant="caption" weight="semibold">
                      {action.title}
                    </Text>
                    <Text variant="eyebrow" tone="secondary">
                      {action.body}
                    </Text>
                  </Stack>
                  <Icon name="arrow_forward" size={16} />
                </Stack>
              </Card>
            ))}
          </Stack>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Recent
            </Text>
            <div className="pb-mt-3">
              {transactions.data?.map((transaction) => (
                <TransactionRow key={transaction.id} transaction={transaction} />
              ))}
            </div>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="dark">
            <Text variant="caption" weight="semibold" tone="onDark">
              What it costs
            </Text>
            <Stack gap={3} className="pb-mt-4">
              {FEES.map((fee) => (
                <Stack key={fee.label} direction="row" justify="between" align="center" gap={3}>
                  <Text variant="caption" tone="onDarkMuted" truncate>
                    {fee.label}
                  </Text>
                  <Text variant="caption" weight="semibold" tone="onDark" numeric>
                    {fee.value}
                  </Text>
                </Stack>
              ))}
            </Stack>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
